import React from 'react';
import { motion } from 'framer-motion';
import { Calculator, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import AcidCostCalculator from '../components/AcidCostCalculator';

const AcidCostCalculatorPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <SEOHead
        title="Sulfuric vs. Sulfur Burner Cost Calculator | CTC Waterworks"
        description="Compare your ongoing sulfuric acid costs with elemental sulfur through a sulfur burner. Calculate potential savings and payback for your irrigation water treatment system."
        canonicalPath="/tools/acid-cost-calculator"
      />
      <Navbar />

      <section className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Calculator size={16} />
              <span>Free Cost Comparison Tool</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Sulfuric vs. Sulfur Burner Cost Calculator
            </h1>

            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              If you're treating high-pH, high-bicarbonate irrigation water with sulfuric acid, you're already paying for acidity every season. This tool helps you compare that ongoing sulfuric acid spend with elemental sulfur feeding a sulfur burner. It's not a formal quote, but it will give you a feel for the economics at your scale.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <AcidCostCalculator sourcePage="calculator-page" />
          </motion.div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl p-8 text-white shadow-xl text-center"
          >
            <h2 className="text-2xl font-bold mb-4">Ready for a Precise Design?</h2>
            <p className="text-xl mb-6 text-white/90">
              When you're ready, we'll take your lab report and system details and give you a precise design and quote for a sulfur burner system.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg"
            >
              <span>Get custom sulfur burner quote</span>
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl p-8 shadow-lg border border-gray-200"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Consider Sulfur Burners?</h3>
            <div className="space-y-4 text-gray-700">
              <p>
                Sulfur burners convert elemental sulfur into sulfurous acid (H₂SO₃) on-site, providing the same water acidification benefits as sulfuric acid at a fraction of the ongoing cost.
              </p>
              <p>
                For growers and irrigation managers treating hundreds of millions of gallons per season, the chemical savings alone can pay for a sulfur burner system in just a few years — while eliminating the safety and handling concerns of concentrated sulfuric acid.
              </p>
              <p>
                <Link to="/sulfur-burners" className="text-blue-600 hover:text-blue-700 font-medium underline">
                  Learn more about CTC sulfur burners
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AcidCostCalculatorPage;
